import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { IntegrationSystem, SelectOption } from '@/shared/types';

import { useAsyncEffect } from '@/hooks/useAsyncEffect';

import { AuthContextInterface } from '@/contexts/Auth/AuthContext';

import { getCitySelect, getCountrySelect, getDistrictSelect, getRegionSelect, getStreetSelect } from '@/api/apiService';
import {
  DocumentTypeEntity,
  AddressFieldName,
  StreetSelectOption,
  AddressType,
  BirthAddress,
  LivingAddress,
  RegisteredAddress,
} from '@/api/types';

import { buildFormDefaultValues } from '../buildFormDefaultValues';
import { DomainEntities, ProfileFormValues } from '../types';

interface Props {
  methods: ReturnType<typeof useForm<ProfileFormValues>>;
  authContext: AuthContextInterface;
  setOptionsCountry: (options: SelectOption[]) => void;
  setOptionsRegion: (options: SelectOption[]) => void;
  setOptionsDistrictReg: (options: SelectOption[]) => void;
  setOptionsCityReg: (options: SelectOption[]) => void;
  setOptionsStreetReg: (options: StreetSelectOption[]) => void;
  setOptionsDistrictLiving: (options: SelectOption[]) => void;
  setOptionsCityLiving: (options: SelectOption[]) => void;
  setOptionsStreetLiving: (options: StreetSelectOption[]) => void;
  passportDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
  regAddressDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
  actualAddressDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
}

interface HookReturn {
  triggerReinit: () => void;
  reinitTriggerCount: number;
}

export const useIniter = ({
  authContext,
  setOptionsCountry,
  setOptionsRegion,
  setOptionsDistrictReg,
  setOptionsCityReg,
  setOptionsStreetReg,
  setOptionsDistrictLiving,
  setOptionsCityLiving,
  setOptionsStreetLiving,
  passportDocumentTypeEntities,
  regAddressDocumentTypeEntities,
  actualAddressDocumentTypeEntities,
  methods,
}: Props): HookReturn => {
  const [reinitTriggerCount, setReinitTriggerCount] = useState(0);

  const triggerReinit = (): void => {
    setReinitTriggerCount((prev) => prev + 1);
  };

  const getPrefilledSettlement = (addressData: BirthAddress | LivingAddress | RegisteredAddress): string => {
    const settlement = addressData?.[AddressFieldName.settlement];
    return typeof settlement !== 'string' ? (settlement?.id ?? '') : settlement;
  };

  useAsyncEffect(async () => {
    const initFunction = async (): Promise<Partial<DomainEntities>> => {
      const regAddress = authContext.addressesData[AddressType.REGISTRATION];
      const livingAddress = authContext.addressesData[AddressType.LIVING];

      const prefilledAddressSettlement = getPrefilledSettlement(regAddress);
      const prefilledAddressSettlementLiving = getPrefilledSettlement(livingAddress);

      const tasks = [
        getCountrySelect(setOptionsCountry),
        getRegionSelect(setOptionsRegion),
        regAddress?.region?.id ? getDistrictSelect(regAddress.region.id, setOptionsDistrictReg) : Promise.resolve(null),
        regAddress?.district?.id ? getCitySelect(regAddress.district.id, setOptionsCityReg) : Promise.resolve(null),
        prefilledAddressSettlement
          ? getStreetSelect(prefilledAddressSettlement, setOptionsStreetReg)
          : Promise.resolve(null),
        livingAddress?.region?.id
          ? getDistrictSelect(livingAddress.region.id, setOptionsDistrictLiving)
          : Promise.resolve(null),
        livingAddress?.district?.id
          ? getCitySelect(livingAddress.district.id, setOptionsCityLiving)
          : Promise.resolve(null),
        prefilledAddressSettlementLiving
          ? getStreetSelect(prefilledAddressSettlementLiving, setOptionsStreetLiving)
          : Promise.resolve(null),
      ] as Array<
        | Promise<SelectOption[]>
        | Promise<Array<SelectOption & { isPayable: boolean }>>
        | Promise<StreetSelectOption[]>
        | Promise<null>
      >;

      const settledResults = await Promise.allSettled(tasks);
      const results = settledResults.map((result) => (result.status === 'fulfilled' ? result.value : null)) as Array<
        SelectOption[] | StreetSelectOption[] | null
      >;

      const resetData: Partial<DomainEntities> = {
        optionsCountry: results[0] || [],
        optionsRegion: results[1] || [],
        ...(regAddress?.region?.id ? { optionsDistrictReg: results[2] } : {}),
        ...(regAddress?.district?.id ? { optionsCityReg: results[3] } : {}),
        ...(prefilledAddressSettlement ? { optionsStreetReg: (results[4] as StreetSelectOption[]) || [] } : {}),
        ...(livingAddress?.region?.id ? { optionsDistrictLiving: results[5] } : {}),
        ...(livingAddress?.district?.id ? { optionsCityLiving: results[6] } : {}),
        ...(prefilledAddressSettlementLiving
          ? { optionsStreetLiving: (results[7] as StreetSelectOption[]) || [] }
          : {}),
        passportDocumentTypeEntities,
        regAddressDocumentTypeEntities,
        actualAddressDocumentTypeEntities,
      };

      methods.reset(buildFormDefaultValues(authContext, resetData));
      return resetData;
    };

    initFunction();
  }, [
    authContext.addressesData[AddressType.REGISTRATION]?.country?.id,
    authContext.addressesData[AddressType.REGISTRATION]?.region?.id,
    authContext.addressesData[AddressType.REGISTRATION]?.district?.id,
    authContext.addressesData[AddressType.REGISTRATION]?.[AddressFieldName.settlement],
    authContext.addressesData[AddressType.LIVING]?.country?.id,
    authContext.addressesData[AddressType.LIVING]?.region?.id,
    authContext.addressesData[AddressType.LIVING]?.district?.id,
    authContext.addressesData[AddressType.LIVING]?.[AddressFieldName.settlement],
    reinitTriggerCount,
  ]);

  return { triggerReinit, reinitTriggerCount };
};
